/**
 * Sending what the device has been holding.
 *
 * <p>One queue, replayed in order, oldest first. Order matters: an answer and the comment
 * that followed it are two operations, and applying them the other way round would leave
 * the comment attached to an answer that was later cleared.</p>
 *
 * <p><b>It stops at the first failure.</b> A request that fails because the signal went is
 * a request that will succeed later, and skipping past it would replay the rest out of
 * order. A request that fails because the server refused it — a submission that is not
 * ready, an answer to a question that no longer exists — would otherwise block the queue for
 * ever, so those are taken out and reported instead of retried.</p>
 *
 * <p><b>Except that stopping for ever is also a way to lose everything.</b> An auditor
 * added a video from the gallery and then a photograph. The video was large, the phone
 * was on mobile data, and the upload failed with something that was not a refusal — so
 * the queue kept it, stopped, and tried the same one again every thirty seconds. The
 * photograph behind it never went, nor did anything else, for the rest of the visit. On
 * screen both were there the whole time, because the screen reads the queue rather than
 * the server. Nothing was lost and nothing had arrived.</p>
 *
 * <p>So a request that keeps failing is set aside after {@link Attempts} tries and the
 * queue carries on past it. Order is still kept: setting aside is rare and loud, and the
 * alternative — an auditor filling in forty questions none of which are being sent — is
 * worse than the two of them arriving out of order.</p>
 */
import { ApiError } from '../api/client';
import { audits } from '../api/audits';
import { cache, outbox, type Pending } from './store';

export interface SyncOutcome {
  sent: number;
  /**
   * Stuck on something the server refused. The auditor has to be told.
   *
   * The refusal is kept whole rather than reduced to its message. The message is the
   * server's own words, which are English, and the screen that shows this is in whatever
   * language the auditor chose — the code and its arguments are what can be translated.
   */
  rejected: { pending: Pending; error: ApiError }[];
  /**
   * Tried enough times that the queue has stopped waiting for it.
   *
   * <p>Still owed and still queued — this is not a refusal and the operation has not
   * been given up on. It is here because an auditor whose evidence is not arriving has
   * to be told, and because everything behind it is now going without it, which is a
   * different state from "no signal" and reads differently on screen.</p>
   */
  stuck: { pending: Pending; reason: string }[];

  /** True when something is still waiting — no signal, most likely. */
  waiting: boolean;
}

/**
 * One replay's outcome, split by whether it is about this audit.
 *
 * <p>There is one queue for every audit on the device and a replay walks all of it, so what
 * it reports is not one visit's news. The audit screen used to take the whole of it as its
 * own: a file refused on visit A was put under the question of visit B that had the same
 * identifier — and two visits on one form share every identifier — so the auditor saw
 * evidence trouble on an audit they had not touched. Measured locally with a refused file
 * queued for A while B was open: B's first question said "Not added" and named A's file.</p>
 */
export function concerning(outcome: SyncOutcome, auditId: string) {
  const ours = (pending: Pending) => pending.auditId === auditId;

  return {
    rejected: outcome.rejected.filter((r) => ours(r.pending)),
    /** Refused for another audit. Out of the queue, so still worth saying — but as theirs. */
    elsewhere: outcome.rejected.filter((r) => !ours(r.pending)),
    stuck: outcome.stuck.filter((s) => ours(s.pending)).length,
  };
}

/**
 * How many times one operation may fail before the queue stops waiting for it.
 *
 * <p>Five, and the number is about minutes rather than attempts: the replay runs every
 * thirty seconds, so this is two and a half minutes of a signal that is not coming back
 * before everything behind it is let through.</p>
 */
export const Attempts = 5;

let running: Promise<SyncOutcome> | null = null;

/**
 * Set when work is queued while a replay is already under way.
 *
 * <p>A replay reads the queue once, at the start. Anything added after that is not in
 * the list it is walking, so without this it waits for the next tick — thirty seconds in
 * which the auditor has chosen a second photograph, watched it appear, and concluded
 * that nothing is being sent. They are right, and it is this flag that was missing.</p>
 */
let again = false;

async function send(pending: Pending): Promise<void> {
  const { auditId, op } = pending;

  switch (op.kind) {
    case 'answer':
      cache.put(await audits.answer(auditId, op.questionId, op.body));
      return;

    case 'notes':
      cache.put(await audits.notes(auditId, op.notes));
      return;

    case 'photo':
      cache.put(
        await audits.addPhoto(auditId, op.questionId, new File([op.blob], op.name, { type: op.type })),
      );
      return;

    case 'removePhoto':
      // A photograph that only ever existed on the device is removed from the queue rather
      // than from the server, which has never heard of it.
      if (op.photoId.startsWith('pending:')) return;
      cache.put(await audits.removePhoto(auditId, op.photoId));
      return;

    case 'sign':
      cache.put(await audits.sign(auditId, op.blob, op.name));
      return;

    case 'removeSignature':
      cache.put(await audits.removeSignature(auditId));
      return;

    case 'submit':
      cache.put(await audits.submit(auditId));
      return;
  }
}

/**
 * The 4xx answers that mean "not now" rather than "no".
 *
 * <p>A refusal is taken out of the queue for good, and the line used to be drawn at every
 * 4xx. Three of them are not about the request at all. <b>401</b>: the session ended and
 * could not be renewed — the photograph is fine, the person has to sign in again, and then
 * it goes. <b>408</b>: the server stopped waiting for a body arriving too slowly, which is
 * a large gallery video on a weak signal. <b>429</b>: too many requests for a moment. Each
 * of those threw the evidence away and reported it as refused, at the top of a page the
 * auditor had scrolled away from.</p>
 */
const NotNow = new Set([401, 408, 429]);

/**
 * Whether the server is telling us the request itself is wrong rather than that it could
 * not be delivered. Those are the two cases that must be handled differently, and the
 * status is the only thing that distinguishes them.
 */
const refused = (error: unknown): error is ApiError =>
  error instanceof ApiError && error.status >= 400 && error.status < 500 && !NotNow.has(error.status);

export async function sync(): Promise<SyncOutcome> {
  // Already going: say so, so that the pass under way runs once more when it finishes.
  // Its list was read before this work existed.
  if (running) {
    again = true;
    return running;
  }

  // One at a time. Two replays of the same queue would send everything twice.
  running = (async () => {
    const rejected: SyncOutcome['rejected'] = [];
    const stuck: SyncOutcome['stuck'] = [];
    let sent = 0;
    let waiting = false;

    try {
      for (const pending of await outbox.all()) {
        try {
          await send(pending);
          await outbox.done(pending.id);
          sent++;
        } catch (error) {
          if (refused(error)) {
            // The server will refuse it again tomorrow. Out of the queue and into the
            // auditor's hands, rather than blocking everything behind it for ever.
            await outbox.done(pending.id);
            rejected.push({ pending, error: error as ApiError });
            continue;
          }

          await outbox.failed(pending, String(error));
          waiting = true;

          // Tried enough times that the rest should stop waiting for it. Kept in the
          // queue — it is still owed and the auditor may yet get a signal — but the
          // walk carries on so that everything behind it can go.
          if (pending.attempts + 1 >= Attempts) {
            stuck.push({ pending, reason: String(error) });
            continue;
          }

          // Not yet. Stopping here is still right for the ordinary case, which is a
          // signal that has gone: the next one would fail too, and in order.
          break;
        }
      }
    } finally {
      running = null;
    }

    return { sent, rejected, stuck, waiting };
  })();

  const outcome = await running;

  // Work arrived while that was running, and it was not in the list being walked.
  if (again) {
    again = false;

    const next = await sync();

    return {
      sent: outcome.sent + next.sent,
      rejected: [...outcome.rejected, ...next.rejected],
      stuck: [...outcome.stuck, ...next.stuck],
      waiting: next.waiting,
    };
  }

  return outcome;
}

/**
 * Replays whenever the browser thinks it is online again, and once on start.
 *
 * <p>navigator.onLine is a weak signal — it says the device has a network, not that the
 * server is reachable through it — so the periodic attempt is the one that actually
 * recovers from a hotel wifi that answers DHCP and nothing else.</p>
 */
export function keepSyncing(onDone: (outcome: SyncOutcome) => void, everyMs = 30_000): () => void {
  let stopped = false;

  const attempt = () => {
    if (stopped) return;
    void sync().then(onDone).catch(() => { /* reported by the outcome, never thrown at the UI */ });
  };

  window.addEventListener('online', attempt);
  const timer = window.setInterval(attempt, everyMs);
  attempt();

  return () => {
    stopped = true;
    window.removeEventListener('online', attempt);
    window.clearInterval(timer);
  };
}
